package com.dualremotetest

import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.exp
import kotlin.math.floor
import kotlin.math.max
import kotlin.math.min
import kotlin.math.pow
import kotlin.math.sin

/**
 * Original retro sound effects and music, synthesized from square, triangle and noise voices in the style of
 * 8-bit consoles. Every note and sound here was written for this project; nothing is sampled or transcribed
 * from another game (including Battle City): the cues fill the same roles as the original's three pieces, but the notes
 * are our own. Plain Kotlin, deterministic, unit-tested on the JVM.
 */
object Chiptune {
    const val RATE = 22_050
    /** Bump when any sound changes, so the app re-renders its cached WAV files. */
    const val VERSION = 3

    private enum class Wave { SQUARE, PULSE25, PULSE12, TRIANGLE }

    fun render(s: Sfx): ShortArray = when (s) {
        Sfx.PLAYER_FIRE -> sound(0.13) {
            tone(0.0, 0.12, 1100.0, 280.0, Wave.PULSE25, 0.7, decay = 18.0)
            noise(0.0, 0.035, 0.35, 9000.0, 6000.0, decay = 60.0)
        }
        Sfx.ENEMY_FIRE -> sound(0.12) {
            tone(0.0, 0.11, 700.0, 220.0, Wave.PULSE12, 0.5, decay = 18.0)
        }
        Sfx.BRICK_HIT -> sound(0.16) {
            noise(0.0, 0.15, 0.8, 5200.0, 1400.0, decay = 22.0)
            tone(0.0, 0.05, 260.0, 120.0, Wave.SQUARE, 0.3, decay = 40.0)
        }
        Sfx.STEEL_HIT -> sound(0.14) {
            tone(0.0, 0.1, 1400.0, 1300.0, Wave.SQUARE, 0.45, decay = 30.0)
            tone(0.0, 0.1, 1870.0, 1750.0, Wave.PULSE25, 0.35, decay = 30.0)
            noise(0.0, 0.04, 0.3, 11000.0, 8000.0, decay = 50.0)
        }
        Sfx.ARMOR_HIT -> sound(0.2) {
            tone(0.0, 0.18, 1760.0, 1700.0, Wave.TRIANGLE, 0.7, decay = 16.0)
            tone(0.0, 0.12, 2637.0, 2600.0, Wave.PULSE12, 0.25, decay = 22.0)
        }
        Sfx.ENEMY_EXPLOSION -> sound(0.6) {
            noise(0.0, 0.58, 0.9, 7000.0, 600.0, decay = 6.0)
            tone(0.0, 0.22, 120.0, 38.0, Wave.SQUARE, 0.55, decay = 9.0)
        }
        Sfx.PLAYER_EXPLOSION -> sound(0.85) {
            noise(0.0, 0.5, 0.8, 6000.0, 400.0, decay = 5.0)
            tone(0.05, 0.75, 520.0, 55.0, Wave.SQUARE, 0.5, decay = 2.5)
        }
        Sfx.HQ_EXPLOSION -> sound(1.5) {
            noise(0.0, 1.45, 0.9, 5000.0, 250.0, decay = 2.2)
            tone(0.0, 0.9, 180.0, 30.0, Wave.SQUARE, 0.6, decay = 2.5)
            tone(0.25, 1.1, 90.0, 28.0, Wave.TRIANGLE, 0.6, decay = 2.0)
        }
        Sfx.POWERUP_APPEAR -> sound(0.26) {
            tone(0.0, 0.08, midi(81), midi(81), Wave.PULSE12, 0.5)
            tone(0.1, 0.14, midi(88), midi(88), Wave.PULSE12, 0.5, decay = 12.0)
        }
        Sfx.POWERUP_PICKUP -> sound(0.5) {
            arpeggio(0.0, 0.045, listOf(72, 76, 79, 84, 76, 79, 84, 88), Wave.PULSE25, 0.5)
            tone(0.36, 0.13, midi(91), midi(91), Wave.TRIANGLE, 0.45, decay = 14.0, vibrato = 12.0)
        }
        Sfx.EXTRA_LIFE -> sound(0.8) {
            arpeggio(0.0, 0.06, listOf(67, 71, 74, 79, 83, 86), Wave.PULSE25, 0.5)
            tone(0.36, 0.42, midi(91), midi(91), Wave.PULSE12, 0.4, decay = 5.0, vibrato = 7.0)
            tone(0.36, 0.42, midi(79), midi(79), Wave.TRIANGLE, 0.5, decay = 5.0)
        }
        Sfx.PAUSE -> sound(0.42) {
            tone(0.0, 0.1, midi(76), midi(76), Wave.PULSE25, 0.5)
            tone(0.12, 0.1, midi(72), midi(72), Wave.PULSE25, 0.5)
            tone(0.24, 0.16, midi(79), midi(79), Wave.PULSE25, 0.5, decay = 10.0)
        }
        Sfx.TALLY_TICK -> sound(0.05) {
            tone(0.0, 0.045, 1320.0, 1320.0, Wave.PULSE25, 0.5, decay = 40.0)
        }
        Sfx.ENGINE_IDLE -> loopSound(0.5) { engine(55.0, 0.5, 0.35, 6.0) }
        Sfx.ENGINE_MOVE -> loopSound(0.4) { engine(85.0, 0.4, 0.55, 15.0) }
    }

    fun music(m: Music): ShortArray = when (m) {
        Music.NONE -> ShortArray(0)
        Music.STAGE_START -> sound(4.4) { stageStart() }
        Music.GAME_OVER -> sound(2.9) { gameOver() }
        Music.HIGH_SCORE -> sound(4.2) { highScore() }
        Music.MENU -> sound(MENU_SECONDS, peak = 0.5f, fadeTail = false) { menu() }
    }

    fun wav(pcm: ShortArray): ByteArray {
        val data = pcm.size * 2
        val out = ByteArray(44 + data)
        fun str(at: Int, s: String) = s.forEachIndexed { i, c -> out[at + i] = c.code.toByte() }
        fun int(at: Int, v: Int) { for (i in 0..3) out[at + i] = (v shr (8 * i)).toByte() }
        fun short(at: Int, v: Int) { out[at] = v.toByte(); out[at + 1] = (v shr 8).toByte() }
        str(0, "RIFF"); int(4, 36 + data); str(8, "WAVE")
        str(12, "fmt "); int(16, 16); short(20, 1); short(22, 1); int(24, RATE); int(28, RATE * 2); short(32, 2); short(34, 16)
        str(36, "data"); int(40, data)
        pcm.forEachIndexed { i, v -> short(44 + 2 * i, v.toInt()) }
        return out
    }

    // ---------------------------------------------------------------- music cues (original compositions)

    /** About 4.3 s: a brisk G-major fanfare for the start of a stage. */
    private fun Mix.stageStart() {
        val e = 0.135
        val lead = listOf(
            67 to 1, 67 to 1, 71 to 1, 74 to 1, 72 to 2, 71 to 1, 69 to 1,
            67 to 1, 69 to 1, 71 to 1, 72 to 1, 74 to 4,
            76 to 1, 74 to 1, 72 to 1, 71 to 1, 69 to 2, 71 to 1, 72 to 1,
            74 to 2, 71 to 2, 67 to 4,
        )
        melody(0.0, e, Wave.PULSE25, 0.42, lead, tail = 0.25)
        melody(0.0, e, Wave.PULSE12, 0.16, lead.map { (n, d) -> n - 12 to d }, tail = 0.25)
        melody(
            0.0, e, Wave.TRIANGLE, 0.55,
            listOf(43 to 2, 50 to 2, 48 to 2, 50 to 2, 43 to 2, 47 to 2, 50 to 4, 48 to 2, 52 to 2, 50 to 2, 54 to 2, 55 to 2, 50 to 2, 43 to 4),
            tail = 0.25,
        )
        for (bar in 0 until 4) {
            val b = bar * 8 * e
            kick(b, 0.45)
            snare(b + 2 * e, 0.3)
            kick(b + 4 * e, 0.35)
            snare(b + 6 * e, 0.3)
        }
        noise(28 * e, 0.5, 0.3, 8000.0, 2500.0, decay = 6.0)
    }

    /**
     * About 19 s, loops: a calm A-minor piece for the title and menus (Am – F – C – G, two bars each) with a soft
     * arpeggio, a slow lead and a triangle bass. Every note has died away before the loop point, so it repeats cleanly.
     */
    private fun Mix.menu() {
        val beat = MENU_SECONDS / 32
        val e = beat / 2
        val chords = listOf(listOf(57, 60, 64, 69), listOf(53, 57, 60, 65), listOf(48, 52, 55, 60), listOf(55, 59, 62, 67))
        chords.forEachIndexed { c, notes ->
            val pattern = listOf(0, 1, 2, 3, 2, 1, 2, 1)
            for (bar in 0 until 2) for (i in 0 until 8) {
                val t = (c * 8 + bar * 4) * beat + i * e
                tone(t, e * 1.8, midi(notes[pattern[i]] + 12), midi(notes[pattern[i]] + 12), Wave.PULSE12, 0.16, decay = 5.0)
            }
            tone(c * 8 * beat, beat * 3.6, midi(notes[0] - 12), midi(notes[0] - 12), Wave.TRIANGLE, 0.5, decay = 0.9)
            tone((c * 8 + 4) * beat, beat * 3.4, midi(notes[0] - 12), midi(notes[0] - 12), Wave.TRIANGLE, 0.45, decay = 1.0)
        }
        val lead = listOf(76 to 2, 74 to 1, 72 to 1, 69 to 4, 72 to 2, 69 to 2, 65 to 4, 67 to 2, 72 to 2, 76 to 4, 74 to 3, 71 to 1, 67 to 4)
        var t = 0.0
        lead.forEach { (n, len) ->
            tone(t, len * beat * 0.95, midi(n), midi(n), Wave.TRIANGLE, 0.32, decay = 0.6, vibrato = 4.0)
            t += len * beat
        }
    }

    private fun Mix.gameOver() {
        val e = 0.16
        melody(0.0, e, Wave.SQUARE, 0.4, listOf(67 to 2, 66 to 2, 65 to 2, 64 to 2, 62 to 7), tail = 0.4, vibrato = 5.0)
        melody(0.0, e, Wave.TRIANGLE, 0.55, listOf(43 to 4, 40 to 4, 38 to 7), tail = 0.4)
    }

    /** About 4 s: a rising C-major figure with a trill on the final note. */
    private fun Mix.highScore() {
        val e = 0.12
        val lead = listOf(
            72 to 1, 76 to 1, 79 to 1, 76 to 1, 81 to 1, 79 to 1, 84 to 2,
            83 to 1, 81 to 1, 79 to 1, 81 to 1, 83 to 2, 86 to 2,
            84 to 1, 86 to 1, 88 to 1, 86 to 1, 84 to 1, 83 to 1, 84 to 6,
        )
        melody(0.0, e, Wave.PULSE25, 0.42, lead, tail = 0.4)
        melody(0.0, e, Wave.PULSE12, 0.15, lead.map { (n, d) -> n - 5 to d }, tail = 0.4)
        melody(0.0, e, Wave.TRIANGLE, 0.55, listOf(48 to 4, 53 to 4, 55 to 4, 43 to 4, 48 to 4, 55 to 2, 48 to 6), tail = 0.4)
        for (i in 0 until 7) snare(i * 4 * e, 0.22)
        noise(22 * e, 0.6, 0.25, 9000.0, 2000.0, decay = 4.0)
    }

    /** Engine rumble for a seamless loop: whole cycles of a low pulse, wobbled at [wobble] Hz, plus quiet noise. */
    private fun Mix.engine(hz: Double, seconds: Double, noiseVol: Double, wobble: Double) {
        val n = buf.size
        val cycles = roundHalfUp(hz * seconds).toDouble()
        val wob = roundHalfUp(wobble * seconds).toDouble()
        var lfsr = 1
        var clock = 0.0
        var nz = 1.0
        for (i in 0 until n) {
            val p = (i * cycles / n) % 1.0
            val sq = if (p < 0.3) 1.0 else -1.0
            val w = 0.75 + 0.25 * sin(2 * PI * wob * i / n)
            clock += 3000.0 / RATE
            while (clock >= 1) {
                clock -= 1
                val bit = (lfsr xor (lfsr shr 1)) and 1
                lfsr = (lfsr shr 1) or (bit shl 14)
                nz = if (lfsr and 1 == 0) 1.0 else -1.0
            }
            buf[i] += ((sq * 0.5 + nz * noiseVol * 0.5) * w).toFloat()
        }
    }

    // ---------------------------------------------------------------- synthesis

    private class Mix(seconds: Double) {
        val buf = FloatArray((seconds * RATE).toInt())
    }

    private fun sound(seconds: Double, peak: Float = PEAK, fadeTail: Boolean = true, build: Mix.() -> Unit): ShortArray {
        val mix = Mix(seconds).apply(build)
        // Normalize to a common peak so levels are set per effect (Sfx.level), then fade the last 5 ms.
        val top = mix.buf.maxOf { abs(it) }.coerceAtLeast(1e-6f)
        val g = peak / top
        val fade = if (fadeTail) (0.005 * RATE).toInt() else 1
        return ShortArray(mix.buf.size) { i ->
            val f = if (i >= mix.buf.size - fade) (mix.buf.size - i).toFloat() / fade else 1f
            (mix.buf[i] * g * f * Short.MAX_VALUE).toInt().coerceIn(-32767, 32767).toShort()
        }
    }

    /** Like [sound] but without the end fade, so the sample loops without a click. */
    private fun loopSound(seconds: Double, build: Mix.() -> Unit): ShortArray {
        val mix = Mix(seconds).apply(build)
        val peak = mix.buf.maxOf { abs(it) }.coerceAtLeast(1e-6f)
        val g = PEAK / peak
        return ShortArray(mix.buf.size) { i -> (mix.buf[i] * g * Short.MAX_VALUE).toInt().coerceIn(-32767, 32767).toShort() }
    }

    private fun midi(n: Int) = 440.0 * 2.0.pow((n - 69) / 12.0)

    /** One voice from [start] for [dur] seconds, gliding from [f0] to [f1] Hz. */
    private fun Mix.tone(
        start: Double, dur: Double, f0: Double, f1: Double, wave: Wave, vol: Double,
        decay: Double = 0.0, vibrato: Double = 0.0, tremolo: Double = 0.0,
    ) {
        val s0 = (start * RATE).toInt()
        val n = min((dur * RATE).toInt(), buf.size - s0)
        var phase = 0.0
        val attack = 0.004 * RATE
        val release = min(0.02 * RATE, n / 3.0)
        for (i in 0 until max(n, 0)) {
            val t = i.toDouble() / RATE
            var f = f0 + (f1 - f0) * i / n
            if (vibrato > 0) f *= 1 + 0.012 * sin(2 * PI * vibrato * t) * min(1.0, t * 6)
            phase += f / RATE
            val p = phase - floor(phase)
            val v = when (wave) {
                Wave.SQUARE -> if (p < 0.5) 1.0 else -1.0
                Wave.PULSE25 -> if (p < 0.25) 1.0 else -1.0
                Wave.PULSE12 -> if (p < 0.125) 1.0 else -1.0
                Wave.TRIANGLE -> 4 * abs(p - 0.5) - 1
            }
            var env = min(1.0, i / attack) * min(1.0, (n - i) / release)
            if (decay > 0) env *= exp(-decay * t)
            if (tremolo > 0) env *= 0.6 + 0.4 * (if (sin(2 * PI * tremolo * t) >= 0) 1 else -1)
            buf[s0 + i] += (v * vol * env).toFloat()
        }
    }

    /** 15-bit LFSR noise like the 8-bit noise channel; its clock glides from [rate0] to [rate1] Hz. */
    private fun Mix.noise(start: Double, dur: Double, vol: Double, rate0: Double, rate1: Double, decay: Double = 0.0) {
        val s0 = (start * RATE).toInt()
        val n = min((dur * RATE).toInt(), buf.size - s0)
        var lfsr = 1
        var clock = 0.0
        var out = 1.0
        for (i in 0 until max(n, 0)) {
            clock += (rate0 + (rate1 - rate0) * i / n) / RATE
            while (clock >= 1) {
                clock -= 1
                val bit = (lfsr xor (lfsr shr 1)) and 1
                lfsr = (lfsr shr 1) or (bit shl 14)
                out = if (lfsr and 1 == 0) 1.0 else -1.0
            }
            val t = i.toDouble() / RATE
            val env = min(1.0, (n - i) / (0.01 * RATE)) * (if (decay > 0) exp(-decay * t) else 1.0)
            buf[s0 + i] += (out * vol * env).toFloat()
        }
    }

    private fun Mix.arpeggio(start: Double, step: Double, notes: List<Int>, wave: Wave, vol: Double) =
        notes.forEachIndexed { i, n -> tone(start + i * step, step * 0.95, midi(n), midi(n), wave, vol) }

    /** Notes as (MIDI note, length in [step]s); the last note rings on for [tail] seconds. */
    private fun Mix.melody(start: Double, step: Double, wave: Wave, vol: Double, notes: List<Pair<Int, Int>>, tail: Double = 0.0, vibrato: Double = 0.0) {
        var t = start
        notes.forEachIndexed { i, (n, len) ->
            val last = i == notes.lastIndex
            val dur = len * step * 0.92 + if (last) tail else 0.0
            tone(t, dur, midi(n), midi(n), wave, vol, decay = if (last) 1.5 else 0.0, vibrato = if (last || len >= 4) vibrato.coerceAtLeast(5.0) else vibrato)
            t += len * step
        }
    }

    private fun Mix.snare(at: Double, vol: Double) = noise(at, 0.09, vol, 9000.0, 6000.0, decay = 30.0)

    private fun Mix.kick(at: Double, vol: Double) = tone(at, 0.09, 140.0, 45.0, Wave.TRIANGLE, vol, decay = 25.0)

    private const val PEAK = 0.8f
    const val MENU_SECONDS = 19.2
}
